import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import AssetImage, { fileUrl, mediaUrl } from "@/components/AssetImage";
import AssetForm from "@/components/AssetForm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  PencilSimple, ArrowsLeftRight, Printer, UploadSimple, FileArrowUp, Tag, Spinner,
  CaretLeft, MapPin, Gauge, FilePdf, Clock, Path, Star, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writeAccess = canWrite(user.role);
  const [asset, setAsset] = useState(null);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statuses, setStatuses] = useState(["Available", "In Service", "Sold", "Pending", "Missing"]);
  const [movements, setMovements] = useState([]);
  const [activities, setActivities] = useState([]);
  const [qr, setQr] = useState(null);
  const [types, setTypes] = useState({});
  const [company, setCompany] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [moveForm, setMoveForm] = useState({ to_location_id: "", reason: "", notes: "" });
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [mileageVal, setMileageVal] = useState("");
  const [mileageDate, setMileageDate] = useState(new Date().toISOString().slice(0, 10));
  const photoRef = useRef();
  const docRef = useRef();

  const load = useCallback(async () => {
    const { data } = await api.get(`/assets/${id}`);
    setAsset(data);
    setNotes(data.notes || "");
    setNewStatus(data.status);
    api.get(`/assets/${id}/movements`).then((r) => setMovements(r.data));
    api.get(`/assets/${id}/activities`).then((r) => setActivities(r.data));
    api.get(`/assets/${id}/qr`).then((r) => setQr(r.data));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/locations").then(({ data }) => setLocations(data));
    api.get("/users").then(({ data }) => setEmployees(data)).catch(() => {});
    api.get("/company").then(({ data }) => { setStatuses(data.statuses || statuses); setCompany(data); }).catch(() => {});
    api.get("/asset-types").then(({ data }) => setTypes(data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const doMove = async () => {
    if (!moveForm.to_location_id) return toast.error("Select a location");
    setBusy(true);
    try {
      await api.post(`/assets/${id}/move`, moveForm);
      toast.success("Asset moved");
      setMoveOpen(false);
      setMoveForm({ to_location_id: "", reason: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doStatus = async () => {
    setBusy(true);
    try {
      await api.post(`/assets/${id}/status`, { status: newStatus });
      toast.success("Status updated");
      setStatusOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveNotes = async () => {
    await api.post(`/assets/${id}/note`, { notes });
    toast.success("Notes saved");
    load();
  };

  const upload = async (file, kind) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/assets/${id}/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "photo" ? "Photo" : "Document"} uploaded`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const updateMileage = async () => {
    setBusy(true);
    try {
      await api.post(`/assets/${id}/mileage`, { mileage: parseInt(mileageVal, 10), date: mileageDate });
      toast.success("Mileage updated");
      setMileageVal("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };


  const deleteFile = async (fileId, kind) => {    setBusy(true);
    try {
      await api.delete(`/assets/${id}/files/${fileId}?kind=${kind}`);
      toast.success(`${kind === "photo" ? "Photo" : "Document"} removed`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setPrimaryPhoto = async (fileId) => {
    setBusy(true);
    try {
      await api.post(`/assets/${id}/photos/${fileId}/primary`);
      toast.success("Cover photo updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const printQr = () => {
    if (!qr) return;
    const logo = company?.logo_url && (company?.qr_settings?.include_company ?? true) ? mediaUrl(company.logo_url) : null;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>QR ${asset.asset_id}</title><style>body{font-family:sans-serif;text-align:center;padding:40px}img.qr{width:280px}img.logo{height:44px;object-fit:contain;margin-bottom:10px}h2{margin:8px 0}p{color:#555;margin:2px}</style></head><body>${logo ? `<div><img class="logo" src="${logo}" crossorigin="anonymous"/></div>` : ""}<h2>${asset.title || asset.asset_id}</h2><p>${asset.asset_id} · Stock ${asset.stock_number||"—"}</p><img class="qr" src="${qr.qr_data_url}"/><p>Scan to view asset</p><script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`);
    w.document.close();
  };

  if (!asset) return <div className="flex items-center justify-center h-96"><Spinner size={32} className="animate-spin text-emerald-600" /></div>;

  const atype = asset.asset_type || "vehicle";
  const title = asset.title || asset.asset_id;
  const typeCfg = types[atype] || { fields: [] };
  const infoFields = atype === "vehicle"
    ? [["Type", asset.type_label], ["Year", asset.year], ["Make", asset.make], ["Model", asset.model], ["Trim", asset.trim], ["VIN", asset.vin], ["Stock #", asset.stock_number], ["Mileage", asset.mileage], ["Color", asset.color], ["Vehicle Type", asset.vehicle_type], ["Location", asset.location_name], ["Assigned", asset.assigned_employee_name]]
    : [["Type", asset.type_label], ["Name", asset.name], ["Stock #", asset.stock_number], ...typeCfg.fields.map((f) => [f.label, (asset.details || {})[f.key]]), ["Location", asset.location_name], ["Assigned", asset.assigned_employee_name]];

  return (
    <div className="space-y-5" data-testid="asset-detail-page">
      <button onClick={() => navigate("/app/inventory")} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900" data-testid="back-to-inventory"><CaretLeft size={16} /> Back to Inventory</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">{title}</h1>
            <StatusBadge status={asset.status} />
          </div>
          <p className="text-zinc-500 text-sm mt-1">{asset.asset_id} · Stock {asset.stock_number || "—"}{asset.identifier ? ` · ${asset.identifier}` : ""}{asset.listing_url && <> · <a href={asset.listing_url} target="_blank" rel="noreferrer" className="text-emerald-700 font-semibold hover:underline" data-testid="asset-listing-link">View on website ↗</a></>}</p>
        </div>
        {writeAccess && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEditOpen(true)} className="h-11 gap-2 border-zinc-300" data-testid="edit-asset"><PencilSimple size={18} /> Edit</Button>
            <Button onClick={() => setMoveOpen(true)} className="h-11 gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="move-asset"><ArrowsLeftRight size={18} /> Move</Button>
            <Button variant="outline" onClick={() => setStatusOpen(true)} className="h-11 gap-2 border-zinc-300" data-testid="change-status"><Tag size={18} /> Status</Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: photo + info */}
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
            <AssetImage path={asset.photos?.[0]?.storage_path} className="w-full h-64 lg:h-80 object-cover" fallbackClassName="w-full h-64 lg:h-80" />
            {asset.photos?.length > 0 && (
              <div className="flex flex-wrap gap-3 p-3" data-testid="photo-gallery">
                {asset.photos.map((p, idx) => (
                  <div key={p.id} className="relative group" data-testid={`photo-thumb-${p.id}`}>
                    <AssetImage path={p.storage_path} className="h-16 w-24 object-cover rounded-md border border-zinc-200" fallbackClassName="h-16 w-24 rounded-md" />
                    {idx === 0 && <span className="absolute top-1 left-1 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded pointer-events-none">COVER</span>}
                    {writeAccess && (
                      <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-md">
                        {idx !== 0 && <button onClick={() => setPrimaryPhoto(p.id)} disabled={busy} title="Set as cover" className="h-7 w-7 flex items-center justify-center bg-white rounded-full text-zinc-700 hover:text-emerald-600" data-testid={`photo-primary-${p.id}`}><Star size={15} weight="fill" /></button>}
                        <button onClick={() => deleteFile(p.id, "photo")} disabled={busy} title="Delete photo" className="h-7 w-7 flex items-center justify-center bg-white rounded-full text-zinc-700 hover:text-red-600" data-testid={`photo-delete-${p.id}`}><Trash size={15} /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white p-5">
            <h3 className="font-display font-bold mb-4">Asset Information</h3>
            <div className="grid sm:grid-cols-3 gap-4 text-sm">
              {infoFields.map(([k, v]) => (
                <div key={k}><p className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">{k}</p><p className="font-medium mt-0.5">{v || "—"}</p></div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="rounded-lg border border-zinc-200 bg-white p-5">
            <h3 className="font-display font-bold mb-3">Notes</h3>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} disabled={!writeAccess} className="focus:ring-2 focus:ring-emerald-500" data-testid="notes-input" />
            {writeAccess && <Button onClick={saveNotes} size="sm" className="mt-3 h-10 bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="save-notes">Save Notes</Button>}
          </div>

          {/* History tabs */}
          <div className="rounded-lg border border-zinc-200 bg-white p-5">
            <Tabs defaultValue="movement">
              <TabsList>
                <TabsTrigger value="movement" data-testid="tab-movement"><Path size={16} className="mr-1.5" /> Movement History</TabsTrigger>
                <TabsTrigger value="activity" data-testid="tab-activity"><Clock size={16} className="mr-1.5" /> Activity Log</TabsTrigger>
              </TabsList>
              <TabsContent value="movement" className="mt-4">
                {movements.length === 0 ? <p className="text-sm text-zinc-400 py-6 text-center">No movements recorded.</p> : (
                  <ol className="relative border-l-2 border-zinc-200 ml-2 space-y-5">
                    {movements.map((m) => (
                      <li key={m.id} className="ml-5" data-testid={`movement-${m.id}`}>
                        <span className="absolute -left-[9px] h-4 w-4 rounded-full bg-emerald-500 border-2 border-white" />
                        <p className="text-sm font-semibold">{m.from_location_name || "New"} → {m.to_location_name}</p>
                        <p className="text-xs text-zinc-500">{new Date(m.created_at).toLocaleString()} · by {m.user_name}</p>
                        {m.reason && <p className="text-xs text-zinc-600 mt-0.5">Reason: {m.reason}</p>}
                        {m.notes && <p className="text-xs text-zinc-600">{m.notes}</p>}
                      </li>
                    ))}
                  </ol>
                )}
              </TabsContent>
              <TabsContent value="activity" className="mt-4">
                {activities.length === 0 ? <p className="text-sm text-zinc-400 py-6 text-center">No activity yet.</p> : (
                  <div className="space-y-3">
                    {activities.map((a) => (
                      <div key={a.id} className="flex gap-3 text-sm">
                        <span className="h-2 w-2 rounded-full bg-zinc-400 mt-1.5 shrink-0" />
                        <div><p><span className="font-semibold">{a.user_name}</span> · {a.detail}</p><p className="text-xs text-zinc-400">{new Date(a.created_at).toLocaleString()}</p></div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-5">
          {/* QR */}
          <div className="rounded-lg border border-zinc-200 bg-white p-5 text-center">
            <h3 className="font-display font-bold mb-3 text-left">QR Code</h3>
            {qr ? <img src={qr.qr_data_url} alt="QR" className="w-40 h-40 mx-auto" data-testid="asset-qr" /> : <Spinner size={24} className="animate-spin mx-auto text-emerald-600" />}
            <Button variant="outline" onClick={printQr} className="mt-3 w-full h-11 gap-2 border-zinc-300" data-testid="print-qr"><Printer size={18} /> Print QR</Button>
          </div>

          {/* Quick facts */}
          <div className="rounded-lg border border-zinc-200 bg-white p-5 space-y-3">
            <div className="flex items-center gap-3"><MapPin size={20} className="text-emerald-600" /><div><p className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Location</p><p className="font-semibold">{asset.location_name || "Unassigned"}</p></div></div>
            <div className="flex items-center gap-3"><Gauge size={20} className="text-emerald-600" /><div><p className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Mileage</p><p className="font-semibold">{asset.mileage || "—"}</p></div></div>
          </div>

          {/* Uploads */}
          {writeAccess && (
            <div className="rounded-lg border border-zinc-200 bg-white p-5 space-y-2">
              <h3 className="font-display font-bold mb-2">Upload</h3>
              <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], "photo")} data-testid="photo-input" />
              <input ref={docRef} type="file" className="hidden" onChange={(e) => upload(e.target.files[0], "document")} data-testid="doc-input" />
              <Button variant="outline" onClick={() => photoRef.current.click()} disabled={busy} className="w-full h-11 gap-2 border-zinc-300" data-testid="upload-photo"><UploadSimple size={18} /> Upload Photo</Button>
              <Button variant="outline" onClick={() => docRef.current.click()} disabled={busy} className="w-full h-11 gap-2 border-zinc-300" data-testid="upload-doc"><FileArrowUp size={18} /> Upload Document</Button>
            </div>
          )}

          {/* Documents */}
          <div className="rounded-lg border border-zinc-200 bg-white p-5">
            <h3 className="font-display font-bold mb-3">Documents</h3>
            {(!asset.documents || asset.documents.length === 0) ? <p className="text-sm text-zinc-400">No documents.</p> : (
              <div className="space-y-2">
                {asset.documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2" data-testid={`doc-${d.id}`}>
                    <a href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-emerald-700 hover:underline min-w-0">
                      <FilePdf size={18} className="shrink-0" /> <span className="truncate">{d.original_filename}</span>
                    </a>
                    {writeAccess && <button onClick={() => deleteFile(d.id, "document")} disabled={busy} className="text-zinc-400 hover:text-red-600 shrink-0" title="Delete document" data-testid={`doc-delete-${d.id}`}><Trash size={16} /></button>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mileage log */}
          <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="mileage-section">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Gauge size={18} className="text-emerald-600" /> Mileage Updates</h3>
            {writeAccess && (
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <Input type="number" min="0" placeholder="New mileage" value={mileageVal} onChange={(e) => setMileageVal(e.target.value)} className="h-11 focus:ring-2 focus:ring-emerald-500" data-testid="mileage-input" />
                <Input type="date" value={mileageDate} onChange={(e) => setMileageDate(e.target.value)} className="h-11 sm:w-40 focus:ring-2 focus:ring-emerald-500" data-testid="mileage-date" />
                <Button onClick={updateMileage} disabled={busy || !mileageVal} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shrink-0" data-testid="mileage-save">Update</Button>
              </div>
            )}
            {(!asset.mileage_history || asset.mileage_history.length === 0) ? (
              <p className="text-sm text-zinc-400">No mileage updates yet. Current: {asset.mileage ? Number(asset.mileage).toLocaleString() : "—"}</p>
            ) : (
              <div className="space-y-2">
                {[...asset.mileage_history].reverse().map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-1.5 last:border-0" data-testid={`mileage-entry-${i}`}>
                    <span className="font-semibold">{Number(m.mileage).toLocaleString()} mi</span>
                    <span className="text-xs text-zinc-400">{m.date}{m.user_name ? ` · ${m.user_name}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AssetForm open={editOpen} onOpenChange={setEditOpen} asset={asset} locations={locations} employees={employees} statuses={statuses} onSaved={load} />

      {/* Move dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">Move Asset</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold">Current Location</Label>
              <p className="text-sm text-zinc-500 mt-1">{asset.location_name || "Unassigned"}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">New Location</Label>
              <Select value={moveForm.to_location_id} onValueChange={(v) => setMoveForm({ ...moveForm, to_location_id: v })}>
                <SelectTrigger className="h-12 mt-1.5" data-testid="move-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-semibold">Reason</Label><Input value={moveForm.reason} onChange={(e) => setMoveForm({ ...moveForm, reason: e.target.value })} className="h-11 mt-1.5" data-testid="move-reason" /></div>
            <div><Label className="text-sm font-semibold">Notes</Label><Textarea value={moveForm.notes} onChange={(e) => setMoveForm({ ...moveForm, notes: e.target.value })} className="mt-1.5" data-testid="move-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)} className="h-11 border-zinc-300">Cancel</Button>
            <Button onClick={doMove} disabled={busy} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="confirm-move">{busy ? <Spinner size={18} className="animate-spin" /> : "Confirm Move"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status dialog */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">Change Status</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label className="text-sm font-semibold">Status</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger className="h-12 mt-1.5" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)} className="h-11 border-zinc-300">Cancel</Button>
            <Button onClick={doStatus} disabled={busy} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="confirm-status">{busy ? <Spinner size={18} className="animate-spin" /> : "Update"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
